import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { useApplicationRolePriorityFieldRenderer } from "../../../../components/Helpers"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { ApplicationRolePost } from "../../../../models/ApplicationRole"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  fullDescription: z.string().optional(),
  isInternal: z.boolean(),
  isDefault: z.boolean(),
  isRequestable: z.boolean(),
  priority: z.number().min(0, "Priority is required"),
})

export function ApplicationRoleCreate() {
  const { applicationId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/applications/${applicationId}#roles`

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, isDirty, isValid, errors },
  } = useForm<ApplicationRolePost>({
    resolver: zodResolver(validationSchema),
  })

  useEffect(() => {
    reset({
      name: "",
      description: "",
      fullDescription: "",
      isInternal: false,
      isDefault: false,
      isRequestable: false,
      priority: 0,
    })
  }, [reset])

  const onSubmit = async (values: ApplicationRolePost) => {
    try {
      const res = await digitalManagerApi.post(
        `/applications/${applicationId}/roles`,
        values
      )
      if (res.status === StatusCodes.CREATED) {
        toast.success("Role created")
        navigate(returnURL)
      }
    } catch {
      toast.error("Cannot create role")
    }
  }

  const handleTextChange = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRolePost,
        "name" | "description" | "fullDescription"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleBooleanToggle = useCallback(
    (
      field: ControllerRenderProps<
        ApplicationRolePost,
        "isInternal" | "isDefault" | "isRequestable"
      >
    ) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<ApplicationRolePost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: ControllerRendererProps<ApplicationRolePost, "description">) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextChange]
  )

  const renderFullDescriptionTextField = useCallback(
    ({
      field,
    }: ControllerRendererProps<ApplicationRolePost, "fullDescription">) => (
      <MUITextField
        id="fullDescription"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.fullDescription?.message === "string"
            ? { fullDescription: { message: errors.fullDescription.message } }
            : undefined
        }
      />
    ),
    [errors.fullDescription, handleTextChange]
  )

  const renderIsInternalCheckbox = useCallback(
    ({ field }: ControllerRendererProps<ApplicationRolePost, "isInternal">) => (
      <MUICheckBox
        id="isInternal"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsDefaultCheckbox = useCallback(
    ({ field }: ControllerRendererProps<ApplicationRolePost, "isDefault">) => (
      <MUICheckBox
        id="isDefault"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsRequestableCheckbox = useCallback(
    ({ field }: ControllerRendererProps<ApplicationRolePost, "isRequestable">) => (
      <MUICheckBox
        id="isRequestable"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<ApplicationRolePost, "priority">) =>
      (value: string) => {
        const parsedValue = value ? Number.parseInt(value, 10) : ""
        field.onChange(parsedValue)
      },
    []
  )

  const renderPriorityTextField = useApplicationRolePriorityFieldRenderer({
    handleParsedNumberChange,
    errors,
  })

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller
          name="description"
          control={control}
          render={renderDescriptionTextField}
        />
      </FieldRow>
      <FieldRow label="Full Description">
        <Controller
          name="fullDescription"
          control={control}
          render={renderFullDescriptionTextField}
        />
      </FieldRow>
      <FieldRow label="Is Internal">
        <Controller
          name="isInternal"
          control={control}
          render={renderIsInternalCheckbox}
        />
      </FieldRow>
      <FieldRow label="Is Default">
        <Controller name="isDefault" control={control} render={renderIsDefaultCheckbox} />
      </FieldRow>
      <FieldRow label="Is Requestable">
        <Controller
          name="isRequestable"
          control={control}
          render={renderIsRequestableCheckbox}
        />
      </FieldRow>
      <FieldRow label="Priority">
        <Controller name="priority" control={control} render={renderPriorityTextField} />
      </FieldRow>
    </FormPage>
  )
}
